import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import Booking from '../../../../core/types/model/Booking';
import EntityName from '../../../../core/enums/EntityName';
import { DATE_FORMAT_ISO } from '../../../../core/constants/date';
import { BookingModalType } from '../../../../core/state/bookings/types';
import { StateStatus } from '../../../../core/enums/StateStatus';
import { formatDate } from '../../../../core/utils/date';
import { isBookingDateValid } from '../../../../core/utils/isBookingDateValid';
import { useValidationNotification } from '../../hooks';

import { RootState } from '../../../../core/state/state';
import { Actions as BookingActions } from '../../../../core/state/bookings/actions';
import { selectProjectSettings } from '../../../../core/state/bookingProjectSettings/selectors';
import { generateEntityCreateAsyncAction } from '../../../../core/state/entity/generateEntityAsyncActions';
import { spawnGlobalNotification } from '../../../../core/state/global-notifications/asyncActions';
import { GlobalNotification, NotificationPosition } from '../../../../core/state/global-notifications/types';

import Modal from '../../../components/modal/Modal';
import Button from '../../../../components/form/button/Button';
import CreateExternalStepOne from './create-external-step-one/CreateExternalStepOne';
import CreateExternalStepTwo from './create-external-step-two/CreateExternalStepTwo';
import CreateExternalStepThree from './create-external-step-three/CreateExternalStepThree';
import { CreateBookingExternalFormData } from './types';
import { Color } from '../../../../core/enums/Color';
import { GlobalNotificationTypes } from '../../../../core/enums/GlobalNotificationTypes';
import { isMobileScreenSize } from '../../../../core/utils/isMobileScreenSize';

const validationSchema = yup
  .object()
  .shape({
    date: yup.date().required(),
    contactPersonId: yup.string().required(),
    transportEquipmentId: yup.number().required(),
    description: yup.string().required(),
    deliveryDuration: yup.number().required()
  })
  .required();

const CreateBookingExternalModal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isMobileScreen = isMobileScreenSize();

  const [step, setStep] = useState<number>(1);

  const { isOpen: isAnyModalOpen, data } = useSelector((state: RootState) => state.bookings.modal);
  const isOpen = isAnyModalOpen && data?.type === BookingModalType.External;
  const initialValues: Partial<CreateBookingExternalFormData> = useMemo(() => data?.initialValues ?? {}, [data?.initialValues]);

  const projectSettings = useSelector(selectProjectSettings);
  const { id: projectId } = projectSettings;
  const userName = useSelector((state: RootState) => state.auth.authInfo?.account.name ?? '');
  const account = useSelector((state: RootState) => state.auth.authInfo?.account);

  const { handleSubmit, control, setValue, watch, reset, formState, getValues } = useForm<CreateBookingExternalFormData>({
    mode: 'onChange',
    defaultValues: {
      additionalTitle: '',
      date: '',
      contactPersonId: null,
      transportEquipmentId: '',
      description: '',
      deliveryZoneId: null,
      deliveryDuration: null,
      startTime: null,
      endTime: null,
      selectedFreeSlot: null,
      activeZone: null
    },
    resolver: yupResolver(validationSchema)
  });
  const { isValid, isSubmitted } = formState;

  const submitStatus = useSelector((state: RootState) => state.bookings.status);
  const deliveryZoneId = watch('deliveryZoneId');
  const errorInputName = useValidationNotification(isOpen, isSubmitted);

  const closeModal = useCallback(() => {
    if (isOpen) {
      setStep(1);
      dispatch(BookingActions.closeModal());
    }
  }, [dispatch, isOpen]);

  const nextStep = useCallback(() => {
    if (step === 1) {
      const { date: deliveryDate } = getValues();

      if (!isBookingDateValid(projectSettings.bookingProjectSettings.bookingDeadline, deliveryDate, dispatch, t)) {
        return;
      }
    }
    setStep(step + 1);
  }, [dispatch, getValues, projectSettings.bookingProjectSettings.bookingDeadline, step, t]);

  const prevStep = useCallback(() => {
    setStep(step - 1);
  }, [step]);

  const onSubmit = useCallback(
    async (formData: CreateBookingExternalFormData) => {
      const createBooking = generateEntityCreateAsyncAction(EntityName.Booking);

      const { additionalTitle, ...rest } = formData;

      let title = `${userName}`;
      if (additionalTitle) {
        title += ` ${additionalTitle}`;
      }

      const booking = {
        ...rest,
        projectId,
        title,
        type: 'EXTERNAL',
        date: formatDate(new Date(formData.date), DATE_FORMAT_ISO),
        contactPersonId: formData.contactPersonId,
        deliveryZoneId: Number(formData.deliveryZoneId)
      } as Partial<Booking>;

      try {
        await dispatch(createBooking(booking as Partial<Booking>));
        const email = account?.userName ?? '';
        if (projectSettings.bookingProjectSettings.isInBarrierSystem === true) {
          const notification: GlobalNotification = {
            type: GlobalNotificationTypes.Success,
            message: (
              <div>
                {t('booking.notification.created.success', { subcontractorEmail: email })
                  .split('\n')
                  .map((item) => (
                    <p>{item}</p>
                  ))}
              </div>
            ),
            title: t('common.notification.success'),
            postition: NotificationPosition.Top,
            delay: 120000
          };
          dispatch(spawnGlobalNotification(notification));
        } else {
          const notification: GlobalNotification = {
            type: GlobalNotificationTypes.Success,
            message: t('booking.notification.created.success.no.barrier.system', { subcontractorEmail: email }),
            title: t('common.notification.success'),
            postition: NotificationPosition.Top,
            delay: 120000
          };
          dispatch(spawnGlobalNotification(notification));
        }
      } catch (e) {
        dispatch(
          spawnGlobalNotification({
            message: t('booking.notification.created.failed'),
            type: GlobalNotificationTypes.Error
          })
        );
      }
    },
    [userName, projectId, dispatch, projectSettings.bookingProjectSettings.isInBarrierSystem, t, account?.userName]
  );

  // Reset form when opened and closed
  useEffect(() => {
    if (isOpen) {
      reset(initialValues);
    }
  }, [isOpen, reset, initialValues, projectSettings]);

  useEffect(() => {
    if (isOpen && isSubmitted && submitStatus === StateStatus.Loaded && step === 3) {
      closeModal();
    }
  }, [closeModal, isOpen, isSubmitted, step, submitStatus]);

  const title = (
    <h3 className="modal-title">
      {t('booking.create.heading')}
      <span className="modal-title--blue">
        {t('common.step')} {`${step}/3`}
      </span>
    </h3>
  );

  const BackButton = () => {
    const Btn = (
      <Button
        className="button--icon-back"
        buttonTypes={!isMobileScreen ? ['outline', 'small'] : ['outline', 'block']}
        color={Color.Blue}
        onClick={prevStep}
        icon="arrow"
        iconLeft
      >
        {t('common.back')}
      </Button>
    );
    return isMobileScreen ? <>{Btn}</> : <div>{Btn}</div>;
  };

  const buttons = (
    <div className="create-booking-modal__buttons justify-content-between">
      {step === 1 && !isMobileScreen && <div />}
      {step !== 1 && <BackButton />}
      <div className={isMobileScreen ? 'w-100 d-flex flex-row justify-content-between modal-buttons' : ''}>
        {(!isMobileScreen || step === 1) && (
          <Button buttonTypes={!isMobileScreen ? ['outline', 'transparent'] : ['outline', 'block']} onClick={closeModal}>
            {t('common.cancel')}
          </Button>
        )}
        {step !== 3 ? (
          <Button
            buttonTypes={!isMobileScreen ? ['blue'] : ['blue', 'block']}
            onClick={nextStep}
            disabled={!isValid || (step === 2 && deliveryZoneId === undefined)}
          >
            {t('common.next')}
          </Button>
        ) : (
          <Button buttonTypes={!isMobileScreen ? ['blue'] : ['blue', 'block']} onClick={handleSubmit(onSubmit)}>
            {t('common.create')}
          </Button>
        )}
      </div>
    </div>
  );

  if (!isOpen) {
    return <></>;
  }

  return (
    <Modal
      isFullScreen={isMobileScreen}
      isOpen={isOpen}
      closeModal={closeModal}
      title={title}
      toolbar={buttons}
      size={step === 2 ? 'large' : 'small'}
      className={isMobileScreen ? 'create-booking-external-modal--mobile p-0' : 'create-booking-external-modal'}
    >
      {step === 1 && <CreateExternalStepOne control={control} errorInputName={errorInputName} setValue={setValue} />}
      {step === 2 && <CreateExternalStepTwo getValues={getValues} control={control} setValue={setValue} />}
      {step === 3 && <CreateExternalStepThree getValues={getValues} onSubmit={onSubmit} handleSubmit={handleSubmit} />}
    </Modal>
  );
};

export default CreateBookingExternalModal;
